/**
 * Server clock synchronization.
 *
 * Overrides Date.now() and new Date() globally so all code in the app
 * uses the server's clock (from clock-service via /ui/clock endpoint).
 * This ensures expiry timers, countdowns, and status checks match the
 * server's time frame instead of the browser's local clock.
 *
 * Handles two modes:
 * - Ticking clock: offset is applied to Date.now() so time advances normally
 * - Frozen clock: Date.now() always returns the frozen server time
 *
 * Call initServerClock() once at app startup, before rendering.
 * It will re-sync every SYNC_INTERVAL_MS in the background.
 */

const SYNC_INTERVAL_MS = 30_000;
const CLOCK_ENDPOINT = "/ui/clock";

const OriginalDate = Date;
const originalNow = Date.now;
window.__realDateNow = originalNow.bind(OriginalDate);
let offset = 0;
let frozen = false;
let frozenTime = 0;

function getServerNow() {
    if (frozen) return frozenTime;
    return originalNow.call(OriginalDate) + offset;
}

function applyOverride() {
    Date.now = getServerNow;

    const PatchedDate = function (...args) {
        if (args.length === 0) {
            return new OriginalDate(getServerNow());
        }
        return new OriginalDate(...args);
    };
    PatchedDate.prototype = OriginalDate.prototype;
    PatchedDate.now = getServerNow;
    PatchedDate.parse = OriginalDate.parse;
    PatchedDate.UTC = OriginalDate.UTC;

    Object.defineProperty(PatchedDate, Symbol.hasInstance, {
        value: (instance) => instance instanceof OriginalDate,
    });

    window.Date = PatchedDate;
}

async function syncOnce() {
    const before = originalNow.call(OriginalDate);
    const response = await fetch(CLOCK_ENDPOINT, { credentials: "include" });
    const after = originalNow.call(OriginalDate);

    if (!response.ok) {
        console.warn("[serverClock] sync failed:", response.status);
        return false;
    }

    const data = await response.json();
    const rtt = after - before;

    frozen = data.frozen;
    if (frozen) {
        frozenTime = data.epochMillis;
        offset = 0;
        console.info(
            "[serverClock] synced (frozen): time=%s rtt=%dms",
            data.iso, rtt
        );
    } else {
        const serverTime = data.epochMillis + rtt / 2;
        offset = serverTime - after;
        console.info(
            "[serverClock] synced: offset=%dms rtt=%dms server=%s",
            Math.round(offset), rtt, data.iso
        );
    }
    return true;
}

let syncInterval = null;

/**
 * Initialize server clock sync. Call once after login. The clock endpoint is
 * cookie-authenticated, so no token is needed.
 */
export async function initServerClock() {
    try {
        await syncOnce();
        applyOverride();
    } catch (e) {
        console.warn("[serverClock] initial sync failed, using local clock:", e.message);
    }

    // Expose for console debugging
    window.__serverClock__ = {
        get offset() { return offset; },
        get frozen() { return frozen; },
        get serverNow() { return new OriginalDate(getServerNow()).toISOString(); },
        get localNow() { return new OriginalDate(originalNow.call(OriginalDate)).toISOString(); },
    };

    // Replace any prior loop so a re-login (initServerClock runs again) does not
    // stack a second poller that then outlives logout.
    stopServerClock();
    syncInterval = setInterval(async () => {
        try {
            await syncOnce();
        } catch (e) {
            console.warn("[serverClock] background sync failed:", e.message);
        }
    }, SYNC_INTERVAL_MS);
}

/**
 * Stop background sync.
 */
export function stopServerClock() {
    if (syncInterval) {
        clearInterval(syncInterval);
        syncInterval = null;
    }
}

/**
 * The browser's own epoch millis, unaffected by the server-clock override.
 *
 * Use this for every duration: a debounce window, a poll deadline, an elapsed
 * measurement. `Date.now()` carries the server's time, which stands still while
 * the server clock is frozen, and a duration measured against a standing clock
 * stays at zero.
 */
export function localNow() {
    return originalNow.call(OriginalDate);
}

/**
 * Get the current offset in milliseconds (server - local).
 */
export function getClockOffset() {
    return offset;
}

/**
 * Whether the server clock is frozen.
 */
export function isClockFrozen() {
    return frozen;
}
